import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import type { ServiceTypeOption, LegalEntitySelectItem, CsvUploadResult, PhoneQuotaInfo } from "../types";
import OveragePlanModal from "./OveragePlanModal";

interface BulkAddSectionProps {
  serviceTypes: ServiceTypeOption[];
  legalEntities: LegalEntitySelectItem[];
  isSysadmin: boolean;
  allowMultipleLegalEntities: boolean;
  onUpload: (file: File, serviceType: number, legalEntityId: number | undefined, updateExisting: boolean) => void;
  isUploading: boolean;
  uploadResult: CsvUploadResult | null;
  quota?: PhoneQuotaInfo;
  isDemo?: boolean;
  remainingCredits?: number;
}

// Special value indicating service type is specified per-row in the CSV
// Backend expects 0 for "Specified in File" — matches MVC inlineRadio4 value="0"
const SPECIFIED_IN_FILE = 0;

const BulkAddSection: React.FC<BulkAddSectionProps> = ({
  serviceTypes,
  legalEntities,
  isSysadmin,
  allowMultipleLegalEntities,
  onUpload,
  isUploading,
  uploadResult,
  quota,
  isDemo = false,
  remainingCredits = 0,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const visibleServiceTypes = serviceTypes.filter(
    (st) => st.isEnabled && (!st.isSysadminOnly || isSysadmin)
  );
  const brandingType = serviceTypes.find((st) => st.isSysadminOnly && st.isEnabled);

  // MVC defaults to Remediation (value=2, inlineRadio2 checked) — match that behavior
  const defaultServiceTypeId = visibleServiceTypes.find((st) => st.id === 2)?.id
    ?? visibleServiceTypes.find((st) => !st.isSysadminOnly)?.id ?? 1;
  const [serviceType, setServiceType] = useState<number>(defaultServiceTypeId);

  // Default to the entity marked isDefault, or first entity if none marked — matches MVC defaultLegalEntityId behavior
  const defaultLegalEntityId = legalEntities.find((le) => le.isDefault)?.legalEntityId
    ?? legalEntities[0]?.legalEntityId;
  const [legalEntityId, setLegalEntityId] = useState<number | undefined>(defaultLegalEntityId);

  // Sync default legal entity when legalEntities load async
  useEffect(() => {
    if (legalEntityId === undefined && defaultLegalEntityId !== undefined) {
      setLegalEntityId(defaultLegalEntityId);
    }
  }, [defaultLegalEntityId]);

  const [updateExisting, setUpdateExisting] = useState(false);
  const [showOverageModal, setShowOverageModal] = useState(false);
  const [overageCount, setOverageCount] = useState(0);
  const [overagePlanLabel, setOveragePlanLabel] = useState("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && !file.name.endsWith(".csv")) {
      toast.error("Please select a CSV file");
      e.target.value = "";
      return;
    }
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      e.target.value = "";
      return;
    }
    if (file) {
      setSelectedFile(file);
      // Auto-submit on file selection — same pattern as MVC and BulkRemoveSection
      triggerUpload(file);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const executeUpload = (file: File) => {
    onUpload(file, serviceType, legalEntityId, updateExisting);
  };

  const triggerUpload = (file: File) => {
    // Quota overage check when a specific service type is selected (not "Specified in File").
    // For "Specified in File", we can't know the per-type breakdown client-side — backend enforces.
    if (quota && serviceType !== SPECIFIED_IN_FILE) {
      // Estimate phone count from CSV file size (read the file to count lines)
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        // Count non-empty lines minus the header row
        const lines = text.split("\n").filter((line) => line.trim() !== "");
        const phoneCount = Math.max(lines.length - 1, 0); // subtract header

        let overage = 0;
        let planLabel = "";
        if (serviceType === 1) {
          overage = (quota.currentMonitoredCount + phoneCount) - quota.monitoredPlanLimit;
          planLabel = "scanning plan subscription";
        } else if (serviceType === 2) {
          overage = (quota.currentRemediationCount + phoneCount) - quota.remediationPlanLimit;
          planLabel = "remediation plan subscription";
        } else if (serviceType === 3) {
          overage = (quota.currentRemediationDailyScanCount + phoneCount) - quota.remediationDailyScanPlanLimit;
          planLabel = "remediation daily scans subscription";
        }

        if (overage > 0) {
          setOverageCount(overage);
          setOveragePlanLabel(planLabel);
          setShowOverageModal(true);
          return;
        }

        executeUpload(file);
      };
      reader.readAsText(file);
      return;
    }

    executeUpload(file);
  };

  // Build sample CSV rows matching MVC handleSampleFormat() + renderCsvRowsToTable()
  // Each row prefers a specific service type, falling back through priority if unavailable
  const buildSampleRows = (): string[][] => {
    const allowedSet = new Set<string>();
    visibleServiceTypes.forEach((st) => allowedSet.add(st.name));
    if (isSysadmin && brandingType) allowedSet.add("Branding (Sysadmin Only)");

    const PRIORITY = ["Scanning", "Remediation", "Remediation Daily Scans", "One Time Scan", "Branding (Sysadmin Only)"];
    const choose = (preferred: string): string => {
      if (allowedSet.has(preferred)) return preferred;
      for (const s of PRIORITY) if (allowedSet.has(s)) return s;
      return "";
    };

    return [
      ["Phone", "Label", "Service Type"],
      ["7074567890", "Sample label 1", choose("Scanning")],
      ["5417543010", "Sample label 2", choose("Remediation")],
      ["8663625478", "Sample label 3", choose("Remediation Daily Scans")],
    ];
  };

  const sampleCsvRows = buildSampleRows();

  const handleDownloadSample = () => {
    const csvContent = sampleCsvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_add.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Build the service type valid values string for instructions
  const allServiceTypeNames = serviceTypes
    .filter((st) => st.isEnabled)
    .map((st) => st.name)
    .join(", ");

  return (
    <div>
      <div className="card">
        <div className="card-header tc-card-header">
          <h5 className="mb-0" style={{ color: "white" }}>Bulk Add Phone Numbers:</h5>
        </div>
        <div className="card-body">
          <div className="row">
            {/* Left column — form */}
            <div className="col-md-6">
              {/* MVC demoCreditsWarning: shown when demo credits exhausted */}
              {isDemo && remainingCredits <= 0 && (
                <div className="alert alert-warning" role="alert" style={{ marginTop: 10, marginBottom: 10 }}>
                  <strong>Demo credits already consumed. Please contact your sales rep to sign up.</strong>
                </div>
              )}
              <p className="fw-bold mb-1">Bulk Import Phone Numbers into TrustCall</p>
              <p className="text-muted mb-3">
                <strong>CSV Format:</strong> Phone, Label, Service Type (Service Type is optional. Only use if{" "}
                <strong>Specified in File</strong> selected below)
              </p>

              {/* Service Type radio buttons */}
              <div className="mb-3">
                <label className="form-label fw-bold">Service Type:</label>
                <div className="d-flex flex-wrap gap-3">
                  {visibleServiceTypes
                    .filter((st) => !st.isSysadminOnly)
                    .map((st) => (
                      <div className="form-check" key={st.id}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="bulkServiceType"
                          id={`bulkSt-${st.id}`}
                          value={st.id}
                          checked={serviceType === st.id}
                          onChange={() => setServiceType(st.id)}
                        />
                        <label className="form-check-label" htmlFor={`bulkSt-${st.id}`}>
                          {st.name}
                        </label>
                      </div>
                    ))}
                  {/* Branding (Sysadmin Only) */}
                  {isSysadmin && brandingType && (
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="bulkServiceType"
                        id={`bulkSt-${brandingType.id}`}
                        value={brandingType.id}
                        checked={serviceType === brandingType.id}
                        onChange={() => setServiceType(brandingType.id)}
                      />
                      <label className="form-check-label" htmlFor={`bulkSt-${brandingType.id}`}>
                        {brandingType.name} (Sysadmin Only)
                      </label>
                    </div>
                  )}
                  {/* Specified in File */}
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="bulkServiceType"
                      id="bulkSt-specified"
                      value={SPECIFIED_IN_FILE}
                      checked={serviceType === SPECIFIED_IN_FILE}
                      onChange={() => setServiceType(SPECIFIED_IN_FILE)}
                    />
                    <label className="form-check-label" htmlFor="bulkSt-specified">
                      Specified in File
                    </label>
                  </div>
                </div>
              </div>

              {/* Legal Entity — label and dropdown in one row matching MVC */}
              {allowMultipleLegalEntities && (
                <div className="d-flex align-items-center gap-2 mb-3">
                  <label className="form-label fw-bold mb-0" style={{ whiteSpace: "nowrap" }}>Legal Entity:</label>
                  <select
                    className="form-select"
                    style={{ maxWidth: 300 }}
                    value={legalEntityId ?? ""}
                    onChange={(e) =>
                      setLegalEntityId(e.target.value ? Number(e.target.value) : undefined)
                    }
                  >
                    <option value="">-- Select --</option>
                    {legalEntities.map((le) => (
                      <option key={le.legalEntityId} value={le.legalEntityId}>
                        {le.legalName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Update existing checkbox */}
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="bulkUpdateExisting"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="bulkUpdateExisting">
                  Update existing numbers with new{" "}
                  {allowMultipleLegalEntities ? "legal entity, " : ""}service type and label
                </label>
              </div>

              {/* Hidden file input + styled button */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div className="d-flex align-items-center gap-2 mb-3">
                <button
                  className="btn"
                  style={{ backgroundColor: "var(--navbar-and-treeview-bg)", borderColor: "var(--navbar-and-treeview-bg)", color: "#fff" }}
                  onClick={handleChooseFile}
                  disabled={isUploading}
                >
                  <svg viewBox="0 0 512 512" width="16" height="16" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}>
                    <path d="M32 384v96h448v-96zm192-64h64V192h96L256 32 128 192h96z" />
                  </svg>
                  <span style={{ verticalAlign: "middle" }}>Choose CSV File</span>
                </button>
                {selectedFile && (
                  <span className="text-muted">{selectedFile.name}</span>
                )}
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className="alert alert-info mt-3">
                  {uploadResult.backgroundMessage ? (
                    <strong>{uploadResult.backgroundMessage}</strong>
                  ) : (
                    <>
                      <strong>Upload Result:</strong> {uploadResult.added} added,{" "}
                      {uploadResult.skipped} skipped
                      {uploadResult.errors.length > 0 && (
                        <div className="mt-2">
                          <strong>Errors ({uploadResult.errors.length}):</strong>
                          <ul className="mb-0 small">
                            {uploadResult.errors.slice(0, 10).map((err, i) => (
                              <li key={i}>
                                Line {err.line}: {err.phone} — {err.reason}
                              </li>
                            ))}
                            {uploadResult.errors.length > 10 && (
                              <li>...and {uploadResult.errors.length - 10} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right column — File Format Instructions (matches MVC: lightgrey rounded box + h4) */}
            <div className="col-md-6">
              <div className="tc-file-format-box" style={{ padding: "0.8rem", borderRadius: "0.8rem" }}>
                <div style={{ marginBottom: 5 }}>
                  <h4>File Format Instructions</h4>
                  <p>
                    Filenames must end with a .csv extension. Column headers must exactly match the
                    format shown in the sample below. The Service Type column is optional - valid
                    values are: {allServiceTypeNames}.
                  </p>
                  <p>
                    Phone numbers should be 10 digits with no formatting characters or leading 1s
                  </p>
                </div>

                {/* Dynamic sample table — matches MVC renderCsvRowsToTable(handleSampleFormat()) */}
                <table className="table table-bordered table-sm tc-table-bordered">
                  <thead>
                    <tr>
                      <th scope="col">Phone</th>
                      <th scope="col">Label</th>
                      <th scope="col">Service Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleCsvRows.slice(1).map((row, i) => (
                      <tr key={i}>
                        <td>{row[0]}</td>
                        <td>{row[1]}</td>
                        <td>{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* DNC-390: this download is a read and must stay usable for Agents, but
                    ActivePhones wraps the section in <fieldset disabled>. Two things fight
                    that: the fieldset itself (so this is an <a>, which the HTML spec does
                    not disable) and Bootstrap's `fieldset:disabled .btn { pointer-events:
                    none }`, which does hit anchors — hence the inline escape below, since
                    inline styles outrank the stylesheet. Removing either breaks the link. */}
                <a
                  href="#"
                  className="btn btn-sm"
                  style={{ width: 200, backgroundColor: "var(--navbar-and-treeview-bg)", borderColor: "var(--navbar-and-treeview-bg)", color: "#fff", pointerEvents: "auto", opacity: 1 }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadSample();
                  }}
                >
                  <svg viewBox="0 0 512 512" width="14" height="14" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}>
                    <path d="M416 32V0h-32v32h-32V0h-32v32h-32V0h-32v32h-32V0h-32v32h-32V0h-32v32H96V0H64v32H32v32H0v32h32v32H0v32h32v32H0v32h32v32H0v32h32v32H0v32h32v32H0v32h32v32h32v32h32v-32h32v32h32v-32h32v32h32v-32h32v32h32v-32h32v32h32v-32h32v32h32v-32h32v-32h32v-32h-32v-32h32v-32h-32v-32h32v-32h-32v-32h32v-32h-32v-32h32v-32h-32V96h32V64h-32V32zm0 384H64V64h352zm32 64v-32h32v32zm32-448h-32V0h32zM0 448h32v32H0zM32 0v32H0V0zm160 128H96v32h96zm192 0H224v32h160zm0 64H224v32h160zm0 64H224v32h160zm0 64H224v32h160zm-192 0H96v32h96zm0-64H96v32h96zm0-64H96v32h96z" />
                  </svg>
                  <span style={{ verticalAlign: "middle" }}>Download Example File</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <OveragePlanModal
        show={showOverageModal}
        overageCount={overageCount}
        planLabel={overagePlanLabel}
        onConfirm={() => {
          setShowOverageModal(false);
          if (selectedFile) executeUpload(selectedFile);
        }}
        onCancel={() => setShowOverageModal(false)}
      />
    </div>
  );
};

export default BulkAddSection;
